import React, { useState, useEffect, useCallback, Fragment } from 'react';
import { useSearchParams, useLocation, useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Paper,
  Button,
  Chip,
  IconButton,
  Alert,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip
} from '@mui/material';
import {
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  Delete as DeleteIcon,
  Cancel as CancelIcon,
  ExpandMore as ExpandMoreIcon,
  KeyboardArrowDown as KeyboardArrowDownIcon
} from '@mui/icons-material';
import { format, addWeeks, subWeeks, startOfWeek, addDays } from 'date-fns';
import { lt } from 'date-fns/locale';
import {
  updateAvailableSlots,
  deleteReservation,
  updateReservation,
  getWeekDates,
  generateTimeSlots,
  AVAILABILITY_STATES,
  getNextAvailabilityStateForCoach,
  LOCATION_INFO,
  LOCATIONS,
  subscribeToAvailableSlotsForWeek,
  subscribeToReservationsForWeek,
  subscribeToAllReservations,
  getCoachByUid,
  DEFAULT_COACH_ID
} from '../utils/firebaseUtils';
import { isPastDateTime } from '../utils/dateUtils';
import { isUserAdmin } from '../utils/adminUtils';
import { useAuth } from '../contexts/AuthContext';
import AdminDashboard from './AdminDashboard';

const AdminPage = () => {
  const { currentUser, approvedCoachUids } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const currentCoach = currentUser ? getCoachByUid(currentUser.uid) : null;
  const hasCoachPanel = currentCoach != null || (approvedCoachUids && approvedCoachUids.includes(currentUser?.uid));
  const currentCoachId = currentCoach?.id ?? (approvedCoachUids?.includes(currentUser?.uid) ? currentUser.uid : DEFAULT_COACH_ID);
  const isSebOnlyCoach = currentCoach?.sebOnly === true;
  const showVrmOption = !isSebOnlyCoach && currentCoachId !== 'elvis';
  const isAdmin = isUserAdmin(currentUser);
  const isAdminOnly = isAdmin && !hasCoachPanel;
  const isVisosRezervacijos = location.pathname.includes('/visos-rezervacijos');
  const isAdministracija = location.pathname.includes('/administracija');
  const reservationsTab = isAdministracija ? 2 : isVisosRezervacijos ? 1 : 0;

  // Admin-only: redirect /admin to dashboard; allow /admin/visos-rezervacijos
  React.useEffect(() => {
    if (isAdminOnly && location.pathname === '/admin') {
      navigate('/admin/administracija', { replace: true });
    }
  }, [isAdminOnly, location.pathname, navigate]);

  const [searchParams, setSearchParams] = useSearchParams();
  
  const getWeekFromUrl = () => {
    const weekParam = searchParams.get('week');
    if (weekParam) {
      try {
        return new Date(weekParam);
      } catch (error) {
        console.error('Invalid week parameter:', weekParam);
      }
    }
    return startOfWeek(new Date(), { weekStartsOn: 1 });
  };
  
  const [currentWeek, setCurrentWeek] = useState(getWeekFromUrl());
  const [availableSlots, setAvailableSlots] = useState({});
  const [weekReservations, setWeekReservations] = useState([]);
  const [loading, setLoading] = useState(true);

  const [allReservations, setAllReservations] = useState([]);
  const [selectedReservation, setSelectedReservation] = useState(null);
  const [reservationDialog, setReservationDialog] = useState(false);
  const [cancelConfirmOpen, setCancelConfirmOpen] = useState(false);
  const [reservationToCancel, setReservationToCancel] = useState(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [reservationToDelete, setReservationToDelete] = useState(null);
  const [showPastReservations, setShowPastReservations] = useState(false);

  const [error, setError] = useState('');

  const weekDates = getWeekDates(format(currentWeek, 'yyyy-MM-dd'));

  // Update URL when week changes
  const updateWeekInUrl = useCallback((newWeek) => {
    const weekString = format(newWeek, 'yyyy-MM-dd');
    setSearchParams({ week: weekString });
  }, [setSearchParams]);

  // Sync URL with current week on initial load
  useEffect(() => {
    const weekParam = searchParams.get('week');
    if (!weekParam) {
      // If no week parameter in URL, set it to current week
      updateWeekInUrl(currentWeek);
    }
  }, [currentWeek, searchParams, updateWeekInUrl]); // Include all dependencies

  const loadWeekData = useCallback(() => {
    setLoading(true);
    try {
      const startDate = format(currentWeek, 'yyyy-MM-dd');
      const cId = currentCoachId || DEFAULT_COACH_ID;
      
      const unsubscribeSlots = subscribeToAvailableSlotsForWeek(startDate, (slots) => {
        setAvailableSlots(slots);
        setLoading(false);
      }, cId);
      
      const unsubscribeReservations = subscribeToReservationsForWeek(startDate, (reservations) => {
        setWeekReservations(reservations);
      }, cId);
      
      return () => {
        unsubscribeSlots();
        unsubscribeReservations();
      };
    } catch (error) {
      console.error('Error setting up week data listeners:', error);
      setError('Klaida kraunant duomenis');
      setLoading(false);
    }
  }, [currentWeek, currentCoachId]);

  const loadAllReservations = useCallback(() => {
    try {
      const cId = isUserAdmin(currentUser) ? null : (currentCoachId || DEFAULT_COACH_ID);
      const unsubscribe = subscribeToAllReservations((reservations) => {
        setAllReservations(reservations);
      }, cId);
      
      return unsubscribe;
    } catch (error) {
      console.error('Error setting up all reservations listener:', error);
    }
  }, [currentUser, currentCoachId]);

  useEffect(() => {
    if (!hasCoachPanel && !(isAdminOnly && isVisosRezervacijos)) {
      setLoading(false);
      return;
    }
    if (isAdminOnly && isVisosRezervacijos) {
      const unsubscribe = loadAllReservations();
      setLoading(false);
      return () => { if (unsubscribe) unsubscribe(); };
    }
    setLoading(true);
    const cleanupWeekData = loadWeekData();
    const cleanupAllReservations = loadAllReservations();
    return () => {
      if (cleanupWeekData) cleanupWeekData();
      if (cleanupAllReservations) cleanupAllReservations();
    };
  }, [hasCoachPanel, isAdminOnly, isVisosRezervacijos, loadWeekData, loadAllReservations]);



  const handleTimeSlotClick = async (date, time) => {
    if (isPastDateTime(date, time)) {
      setError('Negalima keisti praeities laikų');
      return;
    }
    try {
      let currentSlots = availableSlots[date]?.timeSlots;
      
      if (!currentSlots) {
        currentSlots = timeSlots.map(timeSlot => ({
          time: timeSlot,
          availability: AVAILABILITY_STATES.UNAVAILABLE
        }));
      }

      const currentSlot = currentSlots.find(s => s.time === time);
      const currentState = currentSlot?.availability || AVAILABILITY_STATES.UNAVAILABLE;
      const newState = getNextAvailabilityStateForCoach(currentState, currentCoachId);

      const updatedTimeSlots = currentSlots.map(slot => 
        slot.time === time ? { ...slot, availability: newState } : slot
      );

      setAvailableSlots({
        ...availableSlots,
        [date]: {
          date,
          timeSlots: updatedTimeSlots
        }
      });

      await updateAvailableSlots(date, updatedTimeSlots, currentCoachId);
      
      setError('');
      
    } catch (error) {
      console.error('Error saving time slot:', error);
      setError('Klaida išsaugant laiko slotą');
    }
  };





  const handleOpenDeleteConfirm = (reservation, e) => {
    e?.stopPropagation?.();
    setReservationToDelete(reservation);
    setDeleteConfirmOpen(true);
  };

  const handleCloseDeleteConfirm = () => {
    setDeleteConfirmOpen(false);
    setReservationToDelete(null);
  };

  const handleConfirmDelete = async () => {
    if (!reservationToDelete) return;
    try {
      await deleteReservation(reservationToDelete.id);
      handleCloseDeleteConfirm();
      // Real-time listeners will automatically update the UI
    } catch (error) {
      console.error('Error deleting reservation:', error);
      setError('Klaida ištrinant rezervaciją');
    }
  };

  const handleUpdateReservationStatus = async (reservationId, status) => {
    try {
      await updateReservation(reservationId, { status });
      setCancelConfirmOpen(false);
      setReservationToCancel(null);
      // Real-time listeners will automatically update the UI
    } catch (error) {
      console.error('Error updating reservation:', error);
      setError('Klaida atnaujinant rezervaciją');
    }
  };

  const handleOpenCancelConfirm = (reservation, e) => {
    e?.stopPropagation?.();
    setReservationToCancel(reservation);
    setCancelConfirmOpen(true);
  };

  const handleCloseCancelConfirm = () => {
    setCancelConfirmOpen(false);
    setReservationToCancel(null);
  };

  const handleReservationClick = (reservation) => {
    setSelectedReservation(reservation);
    setReservationDialog(true);
  };

  const handleCloseReservationDialog = () => {
    setReservationDialog(false);
    setSelectedReservation(null);
  };

  const getSlotStatus = (date, time) => {
    // Check if this time slot is covered by any reservation
    const isCoveredByReservation = weekReservations.some(reservation => {
      if (reservation.date !== date) return false;
      
      const reservationStart = new Date(`${reservation.date}T${reservation.startTime}`);
      const slotTime = new Date(`${date}T${time}`);
      const reservationEnd = new Date(reservationStart.getTime() + reservation.duration * 60000);
      
      // Check if this time slot falls within the reservation period
      return slotTime >= reservationStart && slotTime < reservationEnd;
    });
    
    if (isCoveredByReservation) return 'booked';
    
    const slot = availableSlots[date]?.timeSlots.find(s => s.time === time);
    if (!slot) return AVAILABILITY_STATES.UNAVAILABLE;
    
    // Use the new availability system
    return slot.availability || AVAILABILITY_STATES.UNAVAILABLE;
  };

  const timeSlots = generateTimeSlots();

  const getSlotColor = (status) => {
    switch (status) {
      case AVAILABILITY_STATES.SEB_ARENA: return '#1976d2'; // Blue for Seb Arena
      case AVAILABILITY_STATES.VRM_COURTS: return '#2e7d32'; // Green for VRM Courts
      case 'booked': return 'error.main';
      case AVAILABILITY_STATES.UNAVAILABLE:
      default: return 'grey.300';
    }
  };

  const getReservationInfo = (date, time) => {
    return weekReservations.find(reservation => {
      if (reservation.date !== date) return false;
      
      const reservationStart = new Date(`${reservation.date}T${reservation.startTime}`);
      const slotTime = new Date(`${date}T${time}`);
      const reservationEnd = new Date(reservationStart.getTime() + reservation.duration * 60000);
      
      return slotTime >= reservationStart && slotTime < reservationEnd;
    });
  };

  const isCurrentWeek = () => {
    const today = new Date();
    const currentWeekStart = startOfWeek(today, { weekStartsOn: 1 });
    
    const selectedWeekStart = currentWeek;
    
    // Check if selected week is in the past
    return selectedWeekStart <= currentWeekStart;
  };

  // Sort and separate reservations by date (exclude preview coach seeded data from admin view)
  const getSortedAndSeparatedReservations = () => {
    const filtered = allReservations.filter((r) => r.coachId !== 'preview');
    if (!filtered.length) return { upcoming: [], past: [] };

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const sortedReservations = [...filtered].sort((a, b) => {
      const dateA = new Date(a.date);
      const dateB = new Date(b.date);
      
      if (dateA.getTime() !== dateB.getTime()) {
        return dateA.getTime() - dateB.getTime();
      }
      
      // If same date, sort by time
      return a.startTime.localeCompare(b.startTime);
    });

    const upcoming = [];
    const past = [];

    sortedReservations.forEach(reservation => {
      const reservationDate = new Date(reservation.date);
      reservationDate.setHours(0, 0, 0, 0);

      if (reservationDate >= today) {
        upcoming.push(reservation);
      } else {
        past.push(reservation);
      }
    });

    // Sort upcoming events: nearest first (ascending order) - same as AdminReservationsLogPage
    upcoming.sort((a, b) => {
      const dateA = new Date(a.date);
      const dateB = new Date(b.date);
      
      if (dateA.getTime() !== dateB.getTime()) {
        return dateA.getTime() - dateB.getTime();
      }
      
      return a.startTime.localeCompare(b.startTime);
    });

    // Sort past events: most recent first (descending order)
    past.sort((a, b) => {
      const dateA = new Date(a.date);
      const dateB = new Date(b.date);
      
      if (dateA.getTime() !== dateB.getTime()) {
        return dateB.getTime() - dateA.getTime(); // Reverse order for descending
      }
      
      // If same date, sort by time (earlier time first)
      return a.startTime.localeCompare(b.startTime);
    });

    return { upcoming, past };
  };

  if (isAdminOnly && !isVisosRezervacijos) {
    return (
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Typography variant="h3" component="h1" sx={{ mb: 4, textAlign: 'center', fontWeight: 600 }}>
          Administracija
        </Typography>
        {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}
        <AdminDashboard />
      </Container>
    );
  }

  if (loading && !(isAdminOnly && isVisosRezervacijos)) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  const pageTitle = isVisosRezervacijos ? 'Visos rezervacijos' : 'Kalendorius';

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      <Typography variant="h3" component="h1" sx={{ mb: 4, textAlign: 'center', fontWeight: 600 }}>
        {pageTitle}
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {reservationsTab === 0 && (
        <>
          {/* Week Navigation - Kalendorius tab */}
          <Box sx={{ 
            display: 'flex', 
            flexDirection: { xs: 'column', sm: 'row' },
            justifyContent: 'space-between', 
            alignItems: { xs: 'stretch', sm: 'center' }, 
            gap: { xs: 2, sm: 0 },
            mb: 3 
          }}>
            {/* First line: Month/Year */}
            <Box sx={{ 
              display: 'flex', 
              flexDirection: { xs: 'row', sm: 'row' },
              alignItems: 'center', 
              justifyContent: { xs: 'center', sm: 'center' },
              gap: { xs: 1, sm: 2 },
              order: { xs: 1, sm: 2 },
              width: { xs: '100%', sm: 'auto' }
            }}>
              <Typography 
                variant="h5" 
                sx={{ 
                  fontWeight: 600,
                  fontSize: { xs: '1.1rem', sm: '1.5rem', md: '1.5rem' },
                  lineHeight: { xs: 1.2, sm: 1.5 }
                }}
              >
                {format(currentWeek, 'MMMM', { locale: lt })} {format(currentWeek, 'd', { locale: lt })} - {format(addDays(currentWeek, 6), 'd', { locale: lt })}, {format(currentWeek, 'yyyy', { locale: lt })}
              </Typography>
            </Box>
            
            {/* Second line: Navigation buttons */}
            <Box sx={{ 
              display: 'flex', 
              flexDirection: { xs: 'row', sm: 'row' },
              justifyContent: { xs: 'space-between', sm: 'center' },
              alignItems: 'center',
              gap: { xs: 0, sm: 2 },
              order: { xs: 2, sm: 1 },
              width: { xs: '100%', sm: 'auto' }
            }}>
              <Button
                startIcon={<ChevronLeftIcon />}
                onClick={() => {
                  const newWeek = subWeeks(currentWeek, 1);
                  setCurrentWeek(newWeek);
                  updateWeekInUrl(newWeek);
                }}
                disabled={isCurrentWeek()}
                sx={{ 
                  minWidth: { xs: 'auto', sm: 'auto' }
                }}
              >
                Ankstesnė savaitė
              </Button>
              
              <Button
                endIcon={<ChevronRightIcon />}
                onClick={() => {
                  const newWeek = addWeeks(currentWeek, 1);
                  setCurrentWeek(newWeek);
                  updateWeekInUrl(newWeek);
                }}
                sx={{ 
                  minWidth: { xs: 'auto', sm: 'auto' }
                }}
              >
                Sekanti savaitė
              </Button>
              
              <Button
                onClick={() => {
                  const currentWeekDate = startOfWeek(new Date(), { weekStartsOn: 1 });
                  setCurrentWeek(currentWeekDate);
                  updateWeekInUrl(currentWeekDate);
                }}
                variant="outlined"
                size="small"
                sx={{ 
                  minWidth: { xs: 'auto', sm: 'auto' },
                  ml: 1
                }}
              >
                Šiandien
              </Button>
            </Box>
          </Box>

          {/* Calendar Container - full height, scrolls with page; compact on mobile */}
          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 4 }}>
            <Paper elevation={3} sx={{ 
              p: { xs: 0.25, sm: 2, md: 3 }, 
              position: 'relative', 
              pl: { xs: 0.5, sm: 3, md: 4 }, 
              maxWidth: 1000,
              width: '100%',
              overflow: 'hidden'
            }}>
              {/* Calendar body: full height; on mobile fits Mon-Sun without horizontal scroll */}
              <Box sx={{ 
                overflowX: 'auto',
                '&::-webkit-scrollbar': {
                  height: 8
                },
                '&::-webkit-scrollbar-track': {
                  bgcolor: 'grey.200'
                },
                '&::-webkit-scrollbar-thumb': {
                  bgcolor: 'grey.400',
                  borderRadius: 4
                }
              }}>
                <Box sx={{ display: 'flex', minWidth: { xs: '100%', sm: 'max-content' } }}>
                  {/* Sticky Time Column - compact on mobile */}
                  <Box sx={{ 
                    position: 'sticky', 
                    left: 0, 
                    zIndex: 5, 
                    bgcolor: 'background.paper',
                    borderRight: '1px solid',
                    borderColor: 'divider',
                    minWidth: { xs: 22, sm: 50, md: 60 },
                    flexShrink: 0
                  }}>
                    {/* Header cell for time column */}
                    <Box sx={{
                      height: { xs: 32, sm: 55, md: 60 },
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      borderBottom: '1px solid',
                      borderColor: 'divider',
                      bgcolor: 'background.paper',
                      position: 'sticky',
                      top: 0,
                      zIndex: 15
                    }}>
                      <Typography variant="subtitle2" sx={{ 
                        fontWeight: 600,
                        fontSize: { xs: '0.55rem', sm: '0.875rem', md: '1rem' }
                      }}>
                        Laikas
                      </Typography>
                    </Box>
                    {timeSlots.map((time, index) => {
                      const isHourEnd = (index + 1) % 2 === 0;
                      const isHalfHour = time.includes(':30');
                      return (
                        <Box
                          key={time}
                          sx={{
                            height: { xs: 18, sm: 28, md: 30 },
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: { xs: '0.55rem', sm: '0.75rem', md: '0.8rem' },
                            color: 'text.secondary',
                            mb: isHourEnd && index < timeSlots.length - 1 ? 0.25 : 0,
                            borderBottom: '1px solid',
                            borderColor: 'divider'
                          }}
                        >
                          {!isHalfHour ? time : ''}
                        </Box>
                      );
                    })}
                  </Box>

                  {/* Day Columns - equal width on mobile so Mon-Sun fit */}
                  {weekDates.map((date, dayIndex) => (
                    <Box
                      key={date}
                      sx={{
                        minWidth: { xs: 0, sm: 100, md: 120 },
                        flex: { xs: 1, sm: 'none' },
                        borderRight: '1px solid',
                        borderColor: 'divider'
                      }}
                    >
                      {/* Header cell for day column */}
                      <Box sx={{
                        height: { xs: 32, sm: 55, md: 60 },
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        borderBottom: '1px solid',
                        borderColor: 'divider',
                        bgcolor: 'background.paper',
                        position: 'sticky',
                        top: 0,
                        zIndex: 10
                      }}>
                        <Typography variant="subtitle2" sx={{ 
                          fontWeight: 600,
                          fontSize: { xs: '0.5rem', sm: '0.8rem', md: '1rem' },
                          textAlign: 'center',
                          lineHeight: 1.1,
                          display: { xs: 'block', sm: 'none' }
                        }}>
                          {format(addDays(currentWeek, dayIndex), 'EEE', { locale: lt })}
                        </Typography>
                        <Typography variant="subtitle2" sx={{ 
                          fontWeight: 600,
                          fontSize: { xs: '0.5rem', sm: '0.8rem', md: '1rem' },
                          textAlign: 'center',
                          lineHeight: 1.1,
                          display: { xs: 'none', sm: 'block' }
                        }}>
                          {format(addDays(currentWeek, dayIndex), 'EEEE', { locale: lt })}
                        </Typography>
                        <Typography variant="body2" sx={{ 
                          fontSize: { xs: '0.45rem', sm: '0.7rem', md: '0.875rem' },
                          textAlign: 'center',
                          lineHeight: 1.1,
                          display: { xs: 'none', sm: 'block' }
                        }} color="text.secondary">
                          {format(addDays(currentWeek, dayIndex), 'd MMM', { locale: lt })}
                        </Typography>
                      </Box>
                      {timeSlots.map((time, index) => {
                        const status = getSlotStatus(date, time);
                        const reservation = getReservationInfo(date, time);
                        const isHourEnd = (index + 1) % 2 === 0;
                        const isPast = isPastDateTime(date, time);
                        return (
                          <Box
                            key={`${date}-${time}`}
                            onClick={() => { if (!isPast) handleTimeSlotClick(date, time); }}
                            sx={{
                              height: { xs: 18, sm: 28, md: 30 },
                              bgcolor: getSlotColor(status),
                              border: '1px solid',
                              borderColor: 'divider',
                              cursor: isPast ? 'not-allowed' : 'pointer',
                              opacity: isPast ? 0.5 : 1,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              mb: isHourEnd && index < timeSlots.length - 1 ? 0.25 : 0,
                              position: 'relative'
                            }}
                          >
                            {status === 'booked' && reservation && (
                              <Typography
                                variant="caption"
                                sx={{
                                  fontSize: { xs: '0.5rem', sm: '0.7rem' },
                                  color: 'white',
                                  fontWeight: 600,
                                  textAlign: 'center',
                                  lineHeight: 1,
                                  maxWidth: '100%',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap'
                                }}
                                title={`${reservation.clientName} - ${reservation.clientPhone}${reservation.courtNo || reservation.sebCourtName ? ` | Aikštelė: ${reservation.courtNo || reservation.sebCourtName}` : ''}`}
                              >
                                {reservation.clientName}
                              </Typography>
                            )}
                          </Box>
                        );
                      })}
                    </Box>
                  ))}
                </Box>
              </Box>
            </Paper>
          </Box>

          {/* Legend – for Seb-only coaches only Seb Arena + unavailable/booked */}
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', mb: 4, flexWrap: 'wrap' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box sx={{ width: 20, height: 20, bgcolor: '#1976d2', borderRadius: 1 }} />
              <Typography variant="body2">
                Laisvas laikas{' '}
                <Typography
                  component="a"
                  href={LOCATION_INFO.seb_arena.googleMapsUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{ 
                    color: '#1976d2', 
                    textDecoration: 'underline',
                    cursor: 'pointer',
                    '&:hover': { color: '#1565c0' }
                  }}
                >
                  Seb Arenoje
                </Typography>
              </Typography>
            </Box>
            {showVrmOption && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box sx={{ width: 20, height: 20, bgcolor: '#2e7d32', borderRadius: 1 }} />
                <Typography variant="body2">
                  Laisvas laikas{' '}
                  <Typography
                    component="a"
                    href={LOCATION_INFO.vrm_courts.googleMapsUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{ 
                      color: '#2e7d32', 
                      textDecoration: 'underline',
                      cursor: 'pointer',
                      '&:hover': { color: '#1b5e20' }
                    }}
                  >
                    VRM teniso kortuose
                  </Typography>
                </Typography>
              </Box>
            )}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box sx={{ width: 20, height: 20, bgcolor: 'error.main', borderRadius: 1 }} />
              <Typography variant="body2">Rezervuota</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box sx={{ width: 20, height: 20, bgcolor: 'grey.300', borderRadius: 1 }} />
              <Typography variant="body2">Nepasiekiama</Typography>
            </Box>
          </Box>
        </>
      )}

      {(reservationsTab === 1 || (isAdminOnly && isVisosRezervacijos)) && (
        <>
          {/* Visos Rezervacijos tab (coaches) or page (admin-only) - all reservations by lesson date, past button */}
          <TableContainer component={Paper} elevation={2} sx={{ borderRadius: 2, overflow: 'auto' }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 700, bgcolor: 'grey.100' }}>Data</TableCell>
                <TableCell sx={{ fontWeight: 700, bgcolor: 'grey.100' }}>Laikas</TableCell>
                {isAdminOnly && isVisosRezervacijos && (
                  <TableCell sx={{ fontWeight: 700, bgcolor: 'grey.100' }}>Treneris</TableCell>
                )}
                <TableCell sx={{ fontWeight: 700, bgcolor: 'grey.100' }}>Klientas</TableCell>
                <TableCell sx={{ fontWeight: 700, bgcolor: 'grey.100' }}>Aikštelė</TableCell>
                <TableCell sx={{ fontWeight: 700, bgcolor: 'grey.100' }}>Būsena</TableCell>
                <TableCell sx={{ fontWeight: 700, bgcolor: 'grey.100', width: 120 }}>Veiksmai</TableCell>
                <TableCell sx={{ fontWeight: 700, bgcolor: 'grey.100', width: 48 }} />
              </TableRow>
            </TableHead>
            <TableBody>
              {(() => {
                const { upcoming, past } = getSortedAndSeparatedReservations();
                const hasUpcoming = upcoming.length > 0;
                const hasPast = past.length > 0;

                const hasCourtLabel = (r) => {
                  const c = r.courtNo || r.sebCourtName;
                  if (r.hasCourt === 'Taip' && c) return `Taip, Nr. ${c}`;
                  if (r.hasCourt === 'Taip') return 'Taip';
                  return 'Ne';
                };

                const colSpan = isAdminOnly && isVisosRezervacijos ? 8 : 7;

                const ReservationRow = ({ r }) => (
                  <TableRow key={r.id} hover>
                    <TableCell>{r.date || '—'}</TableCell>
                    <TableCell>{r.startTime || '—'}</TableCell>
                    {isAdminOnly && isVisosRezervacijos && (
                      <TableCell>{r.coachName || '—'}</TableCell>
                    )}
                    <TableCell>{r.clientName || '—'}</TableCell>
                    <TableCell>{hasCourtLabel(r)}</TableCell>
                    <TableCell>
                      {r.status === 'cancelled' ? (
                        <Chip label="Atšaukta" size="small" color="error" variant="outlined" />
                      ) : (
                        <Chip label="Patvirtinta" size="small" color="success" variant="outlined" />
                      )}
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 0.5 }}>
                        <Tooltip title="Atšaukti rezervaciją" arrow>
                          <span>
                            <IconButton size="small" color="warning" onClick={(e) => { e.stopPropagation(); handleOpenCancelConfirm(r, e); }} disabled={r.status === 'cancelled'} sx={r.status === 'cancelled' ? { color: 'grey.400' } : undefined}>
                              <CancelIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                        <Tooltip title={r.status === 'cancelled' ? 'Ištrinti atšauktą rezervaciją' : 'Įrašą galima ištrinti tik atšaukus'} arrow>
                          <span>
                            <IconButton size="small" color="error" onClick={(e) => { e.stopPropagation(); handleOpenDeleteConfirm(r, e); }} disabled={r.status !== 'cancelled'} sx={r.status !== 'cancelled' ? { color: 'grey.400' } : undefined}>
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <IconButton size="small" color="primary" onClick={() => handleReservationClick(r)} title="Plačiau">
                        <ExpandMoreIcon sx={{ transform: 'rotate(-90deg)' }} />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                );

                if (!hasUpcoming && !hasPast) {
                  return (
                    <TableRow>
                      <TableCell colSpan={colSpan} align="center" sx={{ py: 4, color: 'text.secondary' }}>Rezervacijų nėra.</TableCell>
                    </TableRow>
                  );
                }
                if (!hasUpcoming) {
                  return (
                    <TableRow>
                      <TableCell colSpan={colSpan} align="center" sx={{ py: 4, color: 'text.secondary' }}>Būsimų rezervacijų nėra.</TableCell>
                    </TableRow>
                  );
                }

                return upcoming.map((r) => <ReservationRow key={r.id} r={r} />);
              })()}
            </TableBody>
          </Table>
        </TableContainer>

        {getSortedAndSeparatedReservations().past.length > 0 && (
          <Box sx={{ mt: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'center' }}>
              <Button
                variant="outlined"
                onClick={() => setShowPastReservations(!showPastReservations)}
                endIcon={<KeyboardArrowDownIcon sx={{ transform: showPastReservations ? 'rotate(180deg)' : 'none' }} />}
              >
                {showPastReservations ? 'Slėpti praėjusias rezervacijas' : `Praėjusios rezervacijos (${getSortedAndSeparatedReservations().past.length})`}
              </Button>
            </Box>
            {showPastReservations && (
              <TableContainer component={Paper} elevation={2} sx={{ borderRadius: 2, overflow: 'auto', mt: 2 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 700, bgcolor: 'grey.100' }}>Data</TableCell>
                      <TableCell sx={{ fontWeight: 700, bgcolor: 'grey.100' }}>Laikas</TableCell>
                      {isAdminOnly && isVisosRezervacijos && (
                        <TableCell sx={{ fontWeight: 700, bgcolor: 'grey.100' }}>Treneris</TableCell>
                      )}
                      <TableCell sx={{ fontWeight: 700, bgcolor: 'grey.100' }}>Klientas</TableCell>
                      <TableCell sx={{ fontWeight: 700, bgcolor: 'grey.100' }}>Aikštelė</TableCell>
                      <TableCell sx={{ fontWeight: 700, bgcolor: 'grey.100' }}>Būsena</TableCell>
                      <TableCell sx={{ fontWeight: 700, bgcolor: 'grey.100', width: 120 }}>Veiksmai</TableCell>
                      <TableCell sx={{ fontWeight: 700, bgcolor: 'grey.100', width: 48 }} />
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {getSortedAndSeparatedReservations().past.map((r) => {
                      const hasCourtLabel = (x) => {
                        const c = x.courtNo || x.sebCourtName;
                        if (x.hasCourt === 'Taip' && c) return `Taip, Nr. ${c}`;
                        if (x.hasCourt === 'Taip') return 'Taip';
                        return 'Ne';
                      };
                      return (
                        <TableRow key={r.id} hover>
                          <TableCell>{r.date || '—'}</TableCell>
                          <TableCell>{r.startTime || '—'}</TableCell>
                          {isAdminOnly && isVisosRezervacijos && (
                            <TableCell>{r.coachName || '—'}</TableCell>
                          )}
                          <TableCell>{r.clientName || '—'}</TableCell>
                          <TableCell>{hasCourtLabel(r)}</TableCell>
                          <TableCell>
                            {r.status === 'cancelled' ? (
                              <Chip label="Atšaukta" size="small" color="error" variant="outlined" />
                            ) : (
                              <Chip label="Patvirtinta" size="small" color="success" variant="outlined" />
                            )}
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', gap: 0.5 }}>
                              <Tooltip title="Atšaukti rezervaciją" arrow>
                                <span>
                                  <IconButton size="small" color="warning" onClick={(e) => { e.stopPropagation(); handleOpenCancelConfirm(r, e); }} disabled={r.status === 'cancelled'} sx={r.status === 'cancelled' ? { color: 'grey.400' } : undefined}>
                                    <CancelIcon fontSize="small" />
                                  </IconButton>
                                </span>
                              </Tooltip>
                              <Tooltip title={r.status === 'cancelled' ? 'Ištrinti atšauktą rezervaciją' : 'Įrašą galima ištrinti tik atšaukus'} arrow>
                                <span>
                                  <IconButton size="small" color="error" onClick={(e) => { e.stopPropagation(); handleOpenDeleteConfirm(r, e); }} disabled={r.status !== 'cancelled'} sx={r.status !== 'cancelled' ? { color: 'grey.400' } : undefined}>
                                    <DeleteIcon fontSize="small" />
                                  </IconButton>
                                </span>
                              </Tooltip>
                            </Box>
                          </TableCell>
                          <TableCell>
                            <IconButton size="small" color="primary" onClick={() => handleReservationClick(r)} title="Plačiau">
                              <ExpandMoreIcon sx={{ transform: 'rotate(-90deg)' }} />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Box>
        )}
        </>
      )}

      {reservationsTab === 2 && isUserAdmin(currentUser) && (
        <AdminDashboard />
      )}

      {/* Reservation Details Dialog - same layout as AdminReservationsLogPage */}
      <Dialog open={reservationDialog} onClose={handleCloseReservationDialog} maxWidth="sm" fullWidth>
        {selectedReservation && (
          <>
            <DialogTitle>
              Rezervacija – {selectedReservation.date} {selectedReservation.startTime}
            </DialogTitle>
            <DialogContent>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, pt: 0 }}>
                <Typography variant="body2"><strong>Pamokos data:</strong> {selectedReservation.date} {selectedReservation.startTime}–{selectedReservation.endTime}</Typography>
                <Typography variant="body2"><strong>Klientas:</strong> {selectedReservation.clientName || '—'}</Typography>
                <Typography variant="body2"><strong>Treneris:</strong> {selectedReservation.coachName || '—'}</Typography>
                <Typography variant="body2"><strong>Telefonas:</strong> {selectedReservation.clientPhone || '—'}</Typography>
                <Typography variant="body2"><strong>El. paštas:</strong> {selectedReservation.clientEmail || '—'}</Typography>
                <Typography variant="body2"><strong>Aikštelė:</strong> {selectedReservation.hasCourt === 'Taip' && (selectedReservation.courtNo || selectedReservation.sebCourtName) ? `Taip, Nr. ${selectedReservation.courtNo || selectedReservation.sebCourtName}` : selectedReservation.hasCourt || 'Ne'}</Typography>
                <Typography variant="body2"><strong>Aikštelės nr.:</strong> {selectedReservation.courtNo || selectedReservation.sebCourtName || '—'}</Typography>
                <Typography variant="body2"><strong>Būsena:</strong> {selectedReservation.status === 'cancelled' ? 'Atšaukta' : 'Patvirtinta'}</Typography>
                <Typography variant="body2"><strong>Sukūrimo data:</strong> {selectedReservation.createdAt ? format(selectedReservation.createdAt.toDate?.() ?? selectedReservation.createdAt, 'yyyy-MM-dd HH:mm', { locale: lt }) : '—'}</Typography>
                <Typography variant="body2"><strong>Komentarai:</strong> {selectedReservation.comments || '—'}</Typography>
              </Box>
            </DialogContent>
            <DialogActions>
              <Button onClick={handleCloseReservationDialog}>Uždaryti</Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* Cancel confirmation dialog */}
      <Dialog open={cancelConfirmOpen} onClose={handleCloseCancelConfirm} maxWidth="xs" fullWidth>
        <DialogTitle>Atšaukti rezervaciją?</DialogTitle>
        <DialogContent>
          <Typography>
            Ar tikrai norite atšaukti rezervaciją? Klientui bus išsiųstas el. laiškas apie atšaukimą.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleCloseCancelConfirm} variant="outlined">
            Grįžti
          </Button>
          <Button
            onClick={() => reservationToCancel && handleUpdateReservationStatus(reservationToCancel.id, 'cancelled')}
            variant="contained"
            color="error"
          >
            Atšaukti
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete confirmation dialog */}
      <Dialog open={deleteConfirmOpen} onClose={handleCloseDeleteConfirm} maxWidth="xs" fullWidth>
        <DialogTitle>Ištrinti rezervaciją?</DialogTitle>
        <DialogContent>
          <Typography>
            Ar tikrai norite visam laikui ištrinti šią atšauktą rezervaciją? Šio veiksmo nebus galima atšaukti.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleCloseDeleteConfirm} variant="outlined">
            Grįžti
          </Button>
          <Button onClick={handleConfirmDelete} variant="contained" color="error">
            Ištrinti
          </Button>
        </DialogActions>
      </Dialog>

    </Container>
  );
};

export default AdminPage; 